/**
 * TealerLab — UGC videos (home reel + PDP mini phones)
 *
 * - inline mute/unmute toggle on every UGC video ([data-tlr-ugc-mute]);
 * - shared lightbox: a click on [data-tlr-ugc-open] plays the video full screen
 *   with sound, falling back to muted when the browser refuses it;
 * - ONE sound at a time across every surface: unmuting anywhere mutes the rest,
 *   closing the lightbox mutes everything.
 *
 * Everything is delegated on `document`, so a section re-rendered by the theme
 * editor keeps working without re-binding.
 */

const VIDEO = '[data-tlr-ugc-video]';

// { dialog, stage, triggers, index } while the lightbox is open, else null
let current = null;

function syncToggle(video) {
  const item = video.closest('[data-tlr-ugc-item]');
  const btn = item && item.querySelector('[data-tlr-ugc-mute]');
  if (!btn) return;
  const on = !video.muted;
  btn.classList.toggle('is-unmuted', on);
  btn.setAttribute('aria-pressed', on ? 'true' : 'false');
  const label = on ? btn.dataset.labelMute : btn.dataset.labelUnmute;
  if (label) btn.setAttribute('aria-label', label);
}

function muteAll(except) {
  document.querySelectorAll(VIDEO).forEach((video) => {
    if (video === except) return;
    video.muted = true;
    syncToggle(video);
  });
}

function unmute(video) {
  muteAll(video);
  video.muted = false;
  const played = video.play();
  if (played && played.catch) {
    played.catch(() => {
      // Autoplay policy refused the unmuted playback → keep the loop, muted
      video.muted = true;
      video.play().catch(() => {});
      syncToggle(video);
    });
  }
  syncToggle(video);
}

// Trigger data-attributes are injected in an HTML template literal → escape ALL
// of them, and only let http(s)/relative URLs reach attribute position
function esc(s) {
  return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}
function safeUrl(s) {
  const u = String(s == null ? '' : s).trim();
  return /^(https?:|\/)/i.test(u) ? u : '';
}

function fill(index) {
  if (!current) return;
  const trigger = current.triggers[index];
  if (!trigger) return;
  current.index = index;
  const d = trigger.dataset;
  const video = safeUrl(d.ugcVideo);
  const poster = safeUrl(d.ugcPoster);
  const shopUrl = safeUrl(d.ugcShopUrl);
  const media = video
    ? `<video data-tlr-ugc-video src="${esc(video)}" ${poster ? `poster="${esc(poster)}"` : ''} autoplay loop muted playsinline></video>`
    : poster
      ? `<img src="${esc(poster)}" alt="${esc(d.ugcHandle || d.ugcCaption)}">`
      : '';
  const info = d.ugcHandle || d.ugcCaption
    ? `<div class="tlr-ugc__lb-info">
        ${d.ugcHandle ? `<span class="tlr-ugc__lb-handle">${esc(d.ugcHandle)}</span>` : ''}
        ${d.ugcCaption ? `<p>${esc(d.ugcCaption)}</p>` : ''}
        ${shopUrl && d.ugcShopLabel ? `<a class="tlr-ugc__lb-shop" href="${esc(shopUrl)}">${esc(d.ugcShopLabel)}</a>` : ''}
      </div>`
    : '';
  current.stage.innerHTML = media + info;
  const player = current.stage.querySelector('video');
  // Opening is a user gesture: sound on, and every other video goes silent
  if (player) unmute(player);
}

function cleanup(dialog) {
  const stage = dialog.querySelector('[data-tlr-ugc-stage]');
  if (stage) {
    const video = stage.querySelector('video');
    if (video) video.pause();
    stage.innerHTML = '';
  }
  muteAll();
  if (current && current.dialog === dialog) current = null;
}

function closeLightbox() {
  if (!current) return;
  const dialog = current.dialog;
  cleanup(dialog);
  try { dialog.close(); } catch (_) { dialog.removeAttribute('open'); }
}

// Inline playback paused by openLightbox resumes once the dialog is gone —
// hooked on the dialog 'close' event so Esc/backdrop dismiss resume too.
// Only the cards actually on screen restart. Resuming the whole group would
// wake a card scrolled off the rail, and nothing would ever pause it again:
// the observer only fires on crossing a threshold, and that card no longer
// crosses one, so it would decode for the rest of the session.
document.addEventListener('close', (e) => {
  const dialog = e.target;
  if (!dialog.matches || !dialog.matches('[data-tlr-ugc-lb]')) return;
  const group = dialog.closest('[data-tlr-ugc-group]');
  if (!group) return;
  group.querySelectorAll('[data-tlr-ugc-video]').forEach((v) => {
    if (!v.getAttribute('src')) return;
    const r = v.getBoundingClientRect();
    const onScreen = r.bottom > 0 && r.top < innerHeight && r.right > 0 && r.left < innerWidth;
    if (!onScreen) return;
    try { v.play().catch(() => {}); } catch (_) {}
  });
}, true);

const hasMedia = (el) => Boolean(safeUrl(el.dataset.ugcVideo) || safeUrl(el.dataset.ugcPoster));

function openLightbox(trigger) {
  // A card left empty in the customizer would open a blank dialog
  if (!hasMedia(trigger)) return;
  const group = trigger.closest('[data-tlr-ugc-group]');
  const dialog = group && group.querySelector('[data-tlr-ugc-lb]');
  const stage = dialog && dialog.querySelector('[data-tlr-ugc-stage]');
  if (!stage) return;
  const triggers = Array.from(group.querySelectorAll('[data-tlr-ugc-open]')).filter(hasMedia);
  if (!dialog.dataset.tlrUgcBound) {
    // Native close (Esc, backdrop dismiss) must stop the sound too
    dialog.addEventListener('close', () => cleanup(dialog));
    dialog.dataset.tlrUgcBound = '1';
  }
  current = { dialog, stage, triggers, index: 0 };
  const alone = triggers.length < 2;
  dialog.querySelectorAll('[data-tlr-ugc-prev], [data-tlr-ugc-next]').forEach((btn) => { btn.hidden = alone; });
  // The inline autoplays keep decoding behind the dialog: 4 hidden videos plus
  // the lightbox one on a phone. Pause them while the lightbox is up.
  group.querySelectorAll('[data-tlr-ugc-video]').forEach((v) => { try { v.pause(); } catch (_) {} });
  fill(Math.max(0, triggers.indexOf(trigger)));
  try { dialog.showModal(); } catch (_) { dialog.setAttribute('open', ''); }
}

function step(delta) {
  if (!current) return;
  const total = current.triggers.length;
  if (!total) return;
  fill((current.index + delta + total) % total);
}

document.addEventListener('click', (event) => {
  const toggle = event.target.closest('[data-tlr-ugc-mute]');
  if (toggle) {
    event.preventDefault();
    const item = toggle.closest('[data-tlr-ugc-item]');
    const video = item && item.querySelector(VIDEO);
    if (!video) return;
    if (video.muted) {
      unmute(video);
    } else {
      video.muted = true;
      syncToggle(video);
    }
    return;
  }

  const trigger = event.target.closest('[data-tlr-ugc-open]');
  if (trigger) {
    event.preventDefault();
    openLightbox(trigger);
    return;
  }

  if (!current) return;
  if (event.target === current.dialog) { closeLightbox(); return; }
  if (event.target.closest('[data-tlr-ugc-close]')) { closeLightbox(); return; }
  if (event.target.closest('[data-tlr-ugc-prev]')) { step(-1); return; }
  if (event.target.closest('[data-tlr-ugc-next]')) { step(1); return; }
  // No native controls: tapping the lightbox video toggles pause/play
  const stageVideo = event.target.closest('[data-tlr-ugc-stage] video');
  if (stageVideo) {
    if (stageVideo.paused) stageVideo.play().catch(() => {});
    else stageVideo.pause();
    return;
  }

  // The stage covers the whole dialog, so a click beside the media never
  // reaches the dialog element itself and the backdrop branch above never
  // fires: treat that empty space as a dismiss. The media and the caption
  // block keep their own behaviour.
  const stage = event.target.closest('[data-tlr-ugc-stage]');
  if (stage && stage === current.stage && !event.target.closest('img, .tlr-ugc__lb-info')) {
    closeLightbox();
  }
});

document.addEventListener('keydown', (event) => {
  if (!current) return;
  if (event.key === 'Escape') { closeLightbox(); return; }
  if (event.key === 'ArrowLeft') step(-1);
  if (event.key === 'ArrowRight') step(1);
});

// ---- deferred card playback ----------------------------------------------
// Cards carry no src and no autoplay: the source is attached only when a card
// approaches the viewport, and playback stops when it leaves. Autoplay on a
// card far below the fold made the browser download the whole clip whatever
// preload said — the same 9 MB file was fetched eight times per page load, and
// the product page pulled 248 MB for a block CSS hides under 1024px.
(function () {
  const CARD_VIDEO = '[data-tlr-ugc-video][data-tlr-ugc-src]';
  if (!('IntersectionObserver' in window)) {
    // No observer: attach everything rather than ship silent black cards, and
    // start them too — the markup no longer carries autoplay, so attaching a
    // source alone would leave every card frozen on its poster.
    document.querySelectorAll(CARD_VIDEO).forEach((v) => {
      attach(v);
      try { v.play().catch(() => {}); } catch (_) {}
    });
    return;
  }

  function attach(video) {
    if (video.dataset.tlrUgcLoaded) return;
    video.dataset.tlrUgcLoaded = '1';
    const src = video.getAttribute('data-tlr-ugc-src');
    if (!src) return;
    // Setting src rather than appending a <source> keeps resource selection to
    // one pass. Known limit: a rail clips its own slides, so a card waiting off
    // screen is not intersecting and gets its source only as it slides in. With
    // no poster set on these cards that leaves a blank frame for as long as the
    // file takes to open — the fix is a poster per card, which is content. What a card then costs is the file itself, and these clips are
    // 9 MB each with the same one reused across the four cards: that weight is
    // a catalogue problem, not something this observer can fix.
    video.src = src;
  }

  const io = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      const video = entry.target;
      if (!entry.isIntersecting) {
        try { video.pause(); } catch (_) {}
        return;
      }
      attach(video);
      // A hidden block (the product page hides this under 1024px) never
      // intersects, so it never costs a byte.
      try { video.play().catch(() => {}); } catch (_) {}
    });
  }, { rootMargin: '200px 0px' });

  function observeAll(root) {
    (root || document).querySelectorAll(CARD_VIDEO).forEach((v) => io.observe(v));
  }
  observeAll(document);
  // Sections re-render in the theme editor, and the slider swaps the rail.
  document.addEventListener('shopify:section:load', (e) => observeAll(e.target));
})();
